import { SkyCard, SkyInput, SkyText } from "@skypointcloud/skypoint-ui";
import type { InstanceView } from "@shared/types/instances";
import type { InstanceSettingsView } from "@shared/types/settings";
import { NotificationRulesSummary } from "./NotificationRulesSummary";
import { SecretReferenceField } from "./SecretReferenceField";
import { FORM_MAX_WIDTH } from "@/lib/layout";

/**
 * Instance settings (canvas 08 block 03; FR-57, FR-72).
 *
 * The identity card is read-only on purpose: the instance's name and tenant are
 * set on the platform's Instances page, and a disabled input would drop out of
 * the tab order, so they carry `aria-readonly` and stay reachable instead. It
 * shows no slug and no environment label (round 3, owner 2026-09-30): the slug
 * names the customer's warehouse objects and stays on the platform admin's
 * Instances page, and the deployment is the environment.
 *
 * The Teams webhook is a secret reference here for the same reason it is one on
 * the destination: the URL is a credential, it goes out once and is never read
 * back into this screen (SEC-2).
 */

export interface SettingsDraft {
  retentionDays: string;
  maxConcurrentRuns: string;
  notifyOnSuccess: boolean;
  /** A new webhook URL held for the save, or null to clear. Absent = untouched. */
  teamsWebhookUrl?: string | null;
}

export function draftFromSettings(settings: InstanceSettingsView): SettingsDraft {
  return {
    retentionDays: String(settings.retentionDays),
    maxConcurrentRuns: String(settings.maxConcurrentRuns),
    notifyOnSuccess: settings.notifyOnSuccess,
  };
}

export const MAX_CONCURRENT_RUNS_RANGE = { min: 1, max: 100 } as const;
export const RETENTION_DAYS_RANGE = { min: 30, max: 3650 } as const;

function wholeNumberInRange(text: string, range: { min: number; max: number }): boolean {
  if (text.trim() === "") return false;
  const value = Number(text);
  return Number.isInteger(value) && value >= range.min && value <= range.max;
}

/** The sentence under each out-of-range field (#990), or none when the draft can be saved. */
export function settingsDraftErrors(draft: SettingsDraft): {
  maxConcurrentRuns?: string;
  retentionDays?: string;
} {
  return {
    ...(wholeNumberInRange(draft.maxConcurrentRuns, MAX_CONCURRENT_RUNS_RANGE)
      ? {}
      : { maxConcurrentRuns: "Enter a whole number from 1 to 100." }),
    ...(wholeNumberInRange(draft.retentionDays, RETENTION_DAYS_RANGE)
      ? {}
      : { retentionDays: "Enter a whole number from 30 to 3650." }),
  };
}

export function InstanceSettingsForm({
  instance,
  settings,
  draft,
  readOnly,
  onChange,
  onReplaceWebhook,
}: {
  instance: InstanceView | undefined;
  settings: InstanceSettingsView;
  draft: SettingsDraft;
  readOnly: boolean;
  onChange: (next: SettingsDraft) => void;
  onReplaceWebhook: () => void;
}) {
  const errors = settingsDraftErrors(draft);
  return (
    <div className={`space-y-4 ${FORM_MAX_WIDTH}`}>
      <SkyCard appearance="outline" testId="instance-card" header={{ title: "Instance" }}>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
          <SkyInput
            label="Display name"
            value={instance?.name ?? ""}
            readOnly
            aria-readonly="true"
          />
          <SkyInput
            label="Tenant"
            value={instance?.tenantDisplayName ?? ""}
            readOnly
            aria-readonly="true"
          />
        </div>
      </SkyCard>

      <SkyCard
        appearance="outline"
        testId="runs-retention-card"
        header={{ title: "Runs and retention" }}
      >
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
          <SkyInput
            label="Import limit: source reads and loads at once"
            type="number"
            value={draft.maxConcurrentRuns}
            readOnly={readOnly}
            testId="setting-max-concurrent-runs"
            hint="How many source reads and warehouse loads run at once in this instance. dbt runs one at a time. Between 1 and 100."
            validationState={errors.maxConcurrentRuns ? "error" : "none"}
            validationMessage={errors.maxConcurrentRuns}
            onChange={(event) => onChange({ ...draft, maxConcurrentRuns: event.target.value })}
          />
          <SkyInput
            label="Run history retention (days)"
            type="number"
            value={draft.retentionDays}
            readOnly={readOnly}
            testId="setting-retention-days"
            hint="Between 30 and 3650. Runs and their events older than this are deleted, checked hourly, and are not exported. The audit trail is not affected."
            validationState={errors.retentionDays ? "error" : "none"}
            validationMessage={errors.retentionDays}
            onChange={(event) => onChange({ ...draft, retentionDays: event.target.value })}
          />
        </div>
      </SkyCard>

      <SkyCard appearance="outline" testId="notifications-card" header={{ title: "Notifications" }}>
        <div className="space-y-4">
          <SecretReferenceField
            label="Teams webhook"
            testId="secret-teamsWebhook"
            pointer={settings.teamsWebhook}
            stored={settings.teamsWebhookStored}
            staged={draft.teamsWebhookUrl}
            readOnly={readOnly}
            hint="Stored as a reference. The URL is never read back into this screen."
            onReplace={readOnly ? undefined : onReplaceWebhook}
            onClear={readOnly ? undefined : () => onChange({ ...draft, teamsWebhookUrl: null })}
            onDiscardStaged={
              readOnly
                ? undefined
                : () => {
                    const next = { ...draft };
                    delete next.teamsWebhookUrl;
                    onChange(next);
                  }
            }
          />
          <WebhookHostLine settings={settings} staged={draft.teamsWebhookUrl} />
          {/* #2165: the instance channel and Skypoint's L1 channel told apart, and how to test. */}
          <SkyText as="p" size={200} block testId="teams-webhook-difference">
            This is this instance&apos;s own channel. Rules choose what posts here. Skypoint&apos;s
            L1 team is told separately.
          </SkyText>
          <SkyText as="p" size={200} color="muted" block testId="teams-webhook-test-hint">
            To test it, add a rule on the Notifications tab with this channel on, then use Send a
            test notification.
          </SkyText>
          {/* B-51b: a pointer to the rules, which live on the Notifications tab. */}
          <NotificationRulesSummary />
        </div>
      </SkyCard>
    </div>
  );
}

/** The host of a typed webhook URL, or null while it does not parse. */
function hostOf(url: string): string | null {
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * #1111 (item 4 of the owner decision): where the stored webhook posts, so an Instance admin sees
 * a changed host. The host only, never the path, which carries the webhook's signature.
 */
function WebhookHostLine({
  settings,
  staged,
}: {
  settings: InstanceSettingsView;
  staged: string | null | undefined;
}) {
  let text: string | null = null;
  if (typeof staged === "string") {
    const host = hostOf(staged);
    text = host ? `After you save, alerts post to ${host}.` : null;
  } else if (staged === null) {
    text = "After you save, no Teams webhook is stored.";
  } else if (settings.teamsWebhookStored) {
    text = settings.teamsWebhookHost
      ? `Alerts post to ${settings.teamsWebhookHost}.`
      : "The host this webhook posts to was not recorded. Replace the webhook to record it.";
  }
  if (!text) return null;
  return (
    <SkyText as="p" size={200} block testId="teams-webhook-host">
      {text}
    </SkyText>
  );
}
